import * as React from "react";
import {
  Edit,
  TextInput,
  BooleanInput,
  ReferenceInput,
  usePermissions,
  useRecordContext,
  useSaveContext,
  Toolbar,
  SaveButton,
} from "react-admin";
import {
  validateEmail,
  validateRequired,
  validateUserNote,
} from "../utils/Validations";
import { SimpleForm } from "../common/SimpleForm";
import { DeactivationNoteDialog } from "../common/DeactivationNoteDialog";
import { AutocompleteInputDefaultValue } from "../common/AutocompleteInputDefaultValue";
import { RESTAURANT_TYPES } from "../enums/RestaurantTypes";
import { ROLE_TYPES } from "../enums/Roles";

const UserTitle = () => {
  const record = useRecordContext();
  return (
    <span>
      Usuario {record ? `"${record.firstName} ${record.lastName}"` : ""}
    </span>
  );
};

const UserEditToolbar = () => (
  <Toolbar>
    <SaveButton />
  </Toolbar>
);

const UserEditForm = () => {
  const { permissions } = usePermissions();
  const record = useRecordContext();
  const { save, saving } = useSaveContext();
  const [pendingValues, setPendingValues] = React.useState(null);

  const isAdmin = permissions === ROLE_TYPES.ADMIN;

  const handleSubmit = (values) => {
    if (isAdmin && record?.active && values.active === false) {
      setPendingValues(values);
      return;
    }
    save(values);
  };

  // Awaited so the dialog keeps its spinner and stays open if the save fails.
  const saveWithNote = async (note) => {
    await save({ ...pendingValues, ...(note !== undefined && { note }) });
    setPendingValues(null);
  };

  return (
    <>
      <SimpleForm toolbar={<UserEditToolbar />} onSubmit={handleSubmit}>
        <TextInput
          source="firstName"
          label="Nombre"
          validate={validateRequired}
        />
        <TextInput
          source="lastName"
          label="Apellido"
          validate={validateRequired}
        />
        <TextInput source="email" label="Email" validate={validateEmail} />
        <TextInput
          source="phone"
          label="Teléfono"
          helperText="Ej: +5491123456789"
        />
        <ReferenceInput
          source="restaurant"
          reference="restaurants"
          perPage={100}
          filter={{
            active: true,
            type: RESTAURANT_TYPES.REGULAR,
          }}
        >
          <AutocompleteInputDefaultValue
            permissions={permissions}
            optionText="name"
            filterToQuery={(searchText) => ({ name: searchText })}
            label="Restaurante"
          />
        </ReferenceInput>
        <BooleanInput source="active" label="Activo" />
        {isAdmin && (
          <TextInput
            source="note"
            label="Nota"
            helperText="Anotación interna de administración. El usuario no la ve."
            multiline
            minRows={2}
            fullWidth
            validate={validateUserNote}
          />
        )}
      </SimpleForm>
      <DeactivationNoteDialog
        isOpen={Boolean(pendingValues)}
        initialNote={pendingValues?.note ?? ""}
        loading={saving}
        onConfirm={saveWithNote}
        onClose={() => setPendingValues(null)}
      />
    </>
  );
};

const UserEdit = () => (
  <Edit title={<UserTitle />} mutationMode="pessimistic">
    <UserEditForm />
  </Edit>
);

export default UserEdit;
